import React from "react";
import { RangeSlider } from "rsuite";
import "./CustomSlider.css";

interface ICustomSliderProps {
  pivot: number;
}

const CustomSlider = ({ pivot }: ICustomSliderProps) => {
  // Map values equidistantly
  const mappedMarks = [
    { value: 0, label: "0%" },
    { value: 1, label: "100%" },
    { value: 2, label: "102.5%" },
    { value: 3, label: "105%" },
    { value: 4, label: "107.5%" },
    { value: 5, label: "110%" },
    { value: 6, label: "112.5%" },
    { value: 7, label: "115%" },
    { value: 8, label: "117.5%" },
    { value: 9, label: "120%" },
    { value: 10, label: "125+%" },
  ];

  // const value = (pivot >= 120 ? 121 : pivot);

  const mapSliderValue = (pivot: number) => {
    switch (true) {
      case pivot === 0:
        return 0;
      case pivot > 0 && pivot <= 50:
        return 0.5;
      case pivot > 50 && pivot <= 99:
        return 0.75;
      case pivot === 100:
        return 1;
      // Divide 100 to 102.5 into 5 equal parts
      case pivot > 100 && pivot <= 100.5:
        return 1.2;
      case pivot > 100.5 && pivot <= 101:
        return 1.4;
      case pivot > 101 && pivot <= 101.5:
        return 1.6;
      case pivot > 101.5 && pivot <= 102:
        return 1.8;
      case pivot > 102 && pivot <= 102.5:
        return 2;

      // Divide 102.5 to 105 into 5 equal parts
      case pivot > 102.5 && pivot <= 103:
        return 2.2;
      case pivot > 103 && pivot <= 103.5:
        return 2.4;
      case pivot > 103.5 && pivot <= 104:
        return 2.6;
      case pivot > 104 && pivot <= 104.5:
        return 2.8;
      case pivot > 104.5 && pivot <= 105:
        return 3;

      // Divide 105 to 107.5 into 5 equal parts
      case pivot > 105 && pivot <= 105.5:
        return 3.2;
      case pivot > 105.5 && pivot <= 106:
        return 3.4;
      case pivot > 106 && pivot <= 106.5:
        return 3.6;
      case pivot > 106.5 && pivot <= 107:
        return 3.8;
      case pivot > 107 && pivot <= 107.5:
        return 4;

      // Divide 107.5 to 110 into 5 equal parts
      case pivot > 107.5 && pivot <= 108:
        return 4.2;
      case pivot > 108 && pivot <= 108.5:
        return 4.4;
      case pivot > 108.5 && pivot <= 109:
        return 4.6;
      case pivot > 109 && pivot <= 109.5:
        return 4.8;
      case pivot > 109.5 && pivot <= 110:
        return 5;

      // Divide 110 to 112.5 into 5 equal parts
      case pivot > 110 && pivot <= 110.5:
        return 5.2;
      case pivot > 110.5 && pivot <= 111:
        return 5.4;
      case pivot > 111 && pivot <= 111.5:
        return 5.6;
      case pivot > 111.5 && pivot <= 112:
        return 5.8;
      case pivot > 112 && pivot <= 112.5:
        return 6;

      // Divide 112.5 to 115 into 5 equal parts
      case pivot > 112.5 && pivot <= 113:
        return 6.2;
      case pivot > 113 && pivot <= 113.5:
        return 6.4;
      case pivot > 113.5 && pivot <= 114:
        return 6.6;
      case pivot > 114 && pivot <= 114.5:
        return 6.8;
      case pivot > 114.5 && pivot <= 115:
        return 7;

      // Divide 115 to 117.5 into 5 equal parts
      case pivot > 115 && pivot <= 115.5:
        return 7.2;
      case pivot > 115.5 && pivot <= 116:
        return 7.4;
      case pivot > 116 && pivot <= 116.5:
        return 7.6;
      case pivot > 116.5 && pivot <= 117:
        return 7.8;
      case pivot > 117 && pivot <= 117.5:
        return 8;

      // Divide 117.5 to 120 into 5 equal parts
      case pivot > 117.5 && pivot <= 118:
        return 8.2;
      case pivot > 118 && pivot <= 118.5:
        return 8.4;
      case pivot > 118.5 && pivot <= 119:
        return 8.6;
      case pivot > 119 && pivot <= 119.5:
        return 8.8;
      case pivot > 119.5 && pivot <= 120:
        return 9;

      // Divide 120 to 125 into 5 equal parts
      case pivot > 120 && pivot <= 121:
        return 9.2;
      case pivot > 121 && pivot <= 122:
        return 9.4;
      case pivot > 122 && pivot <= 123:
        return 9.6;
      case pivot > 123 && pivot <= 124:
        return 9.8;
      case pivot > 124 && pivot <= 125:
        return 10;

      // Handle anything above 125
      case pivot > 125:
        return 10;
    }
  };

  const mapWidthToValue = (pivot: number) => {
    switch (true) {
      case pivot === 0:
        return 100;
      case pivot > 0 && pivot <= 100:
        return 92.5;
      case pivot > 100 && pivot <= 102.5:
        return 95;
      case pivot > 102.5 && pivot <= 105:
        return 95;
      case pivot > 105 && pivot <= 107.5:
        return 95;
      case pivot > 107.5 && pivot <= 110:
        return 96;
      case pivot > 110 && pivot <= 112.5:
        return 96;
      case pivot > 112.5 && pivot <= 115:
        return 96;
      case pivot > 115 && pivot <= 117.5:
        return 96;
      case pivot > 117.5 && pivot <= 120:
        return 96;
      case pivot > 120 && pivot <= 125:
        return 96;
      case pivot > 125:
        return 96;
    }
  };

  const mappedValue = mapSliderValue(pivot) || 0;

  const mappedWidthValue = (mapWidthToValue(pivot) || 100) + "%";
  return (
    <div className="slider-wrapper">
      <RangeSlider
        min={0}
        max={10}
        value={[mappedValue, mappedValue]}
        step={0.01}
        graduated
        handleClassName="triangle-handle"
        handleTitle={<span className="perecentage-label">{pivot}%</span>}
        renderMark={(markValue) => {
          const mark = mappedMarks.find((m) => m.value === markValue);
          if (mark) {
            return (
              <span
                style={{
                  marginInline: "7px",
                  display: "inline-block",
                  transform: "translateX(-50%)",
                }}
              >
                {mark.label}
              </span>
            );
          }
          return null;
        }}
        style={{
          width: mappedWidthValue,
        }}
        className="pl-[8.5vw]"
        readOnly // Makes slider readonly
        tooltip={false}
      />
    </div>
  );
};

export default CustomSlider;
